import React from 'react';
import { Block, BlockType } from '../types/block';
import { SwipableHomepageCarousel } from './elements/main-carousel';
import { SectionAppStoresBanner } from './sections/section-app-stores-banner';
import { HeroWithCTABlock } from './sections/hero-with-cta-block';
import { SectionTangibleResults } from './sections/section-tangible-results';
import { SectionViblyStats } from './sections/section-vibly-stats';
import { SectionFeaturedCourses } from './sections/section-featured-courses';
import { SectionUniqueSubscription } from './sections/section-unique-subscription';
import { SectionCompetitiveAdvantages } from './sections/section-competitive-advantages';
import { SectionBestTeachers } from './sections/section-best-teachers';
import { SectionReviews } from './sections/section-reviews';
import { SectionViblyProblemsSolutions } from './sections/section-vibly-problems-solutions';
import { SectionCallToAction } from './sections/section-call-to-action';
import { SectionFrequentlyAskedQuestions } from './sections/section-frequently-asked-questions';
import { SectionPricesCourseCards } from './sections/section-prices-course-cards';
import { SectionPricesSubscriptionsFeatures } from './sections/section-prices-subscriptions-features';
import { SectionPricesList } from './sections/section-prices-list';
import { RichTextWithImageBlock } from './sections/rich-text-with-image';
import { EventBus } from '@/shared/utils';
import { DynamicPageEvents } from 'src/components/containers/dynamic-page/events';

interface BlocksRendererProps {
  content: Block[];
  filter?: { keys: BlockType[]; include?: boolean };
  eventBus: EventBus<DynamicPageEvents>;
}

const getUid = (block: any) => `${block.__component}_${block.id}`;

export const BlocksRenderer: React.FC<BlocksRendererProps> = ({ content, filter, eventBus }) => {
  if (!content || content.length === 0) return null;

  const filteredContent =
    filter && filter.keys.length > 0
      ? content.filter((block) =>
          filter.include === true
            ? filter.keys.includes(block.__component)
            : !filter.keys.includes(block.__component),
        )
      : content;

  return filteredContent.map((block) => {
    switch (block.__component as BlockType) {
      case 'blocks.carousel':
        return <SwipableHomepageCarousel key={getUid(block)} slides={block.slider} />;
      case 'blocks.app-stores-banner':
        return (
          <SectionAppStoresBanner
            key={getUid(block)}
            ctaButtons={block.storesCallToActions}
            className="justify-center flex-nowrap px-4 gap-2"
            ctaClassName="w-full sm:h-[52px] md:w-auto max-w-[180px]"
          />
        );
      case 'blocks.hero-with-cta':
        return <HeroWithCTABlock key={getUid(block)} element={block} />;
      case 'blocks.tangible-results':
        return <SectionTangibleResults key={getUid(block)} element={block} />;
      case 'blocks.vibly-stats':
        return <SectionViblyStats key={getUid(block)} element={block} />;
      case 'blocks.starting-paths':
        return <SectionFeaturedCourses key={getUid(block)} element={block} />;
      case 'blocks.competitive-advantages':
        return <SectionCompetitiveAdvantages key={getUid(block)} element={block} />;
      case 'blocks.subscription':
        return <SectionUniqueSubscription key={getUid(block)} element={block} />;
      case 'blocks.teachers':
        return <SectionBestTeachers key={getUid(block)} element={block} eventBus={eventBus} />;
      case 'blocks.cards-with-trust-pilot':
        return <SectionReviews key={getUid(block)} element={block} />;
      case 'blocks.problems-solutions':
        return <SectionViblyProblemsSolutions key={getUid(block)} element={block} />;
      case 'blocks.call-to-action':
        return <SectionCallToAction key={getUid(block)} element={block} />;
      case 'blocks.faq':
        return <SectionFrequentlyAskedQuestions key={getUid(block)} element={block} />;
      case 'blocks.course-cards':
        return <SectionPricesCourseCards key={getUid(block)} element={block} />;
      case 'blocks.catalog-community':
        return <SectionPricesSubscriptionsFeatures key={getUid(block)} element={block} />;
      case 'blocks.subscription-cards':
        return eventBus ? (
          <SectionPricesList key={getUid(block)} element={block} eventBus={eventBus} />
        ) : null;
      case 'blocks.rich-text':
        return <RichTextWithImageBlock key={getUid(block)} element={block} />;

      default:
        return <p key={getUid(block)}>Unknown block type: {block.__component}</p>;
    }
  });
};
